import { Button, Container, Typography} from '@mui/material'
import { useTranslation } from "react-i18next"

import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import Spacer from "../../../atoms/Spacer/Spacer"

import { logLandingPageAction } from "../../../../clients/logger"
import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"

import styles from './SubscriptionGiftRecipientDetails.module.css'
import SubscriptionGiftStepperStep from "../../../molecules/SubscriptionGiftStepper/SubscriptionGiftStepper"
import useGoogleTagManager from "../../../../helpers/analytics/use-google-tag-manager"
import GtmEventName from "../../../../helpers/analytics/GtmEventName"
import CustomInput from '../../../atoms/CustomInput/CustomInput'
import { useNavigate } from "react-router-dom"
import moment from 'moment'
import { useContext, useState } from 'react'
import SUBSCRIPTION_GIFT_FORM from '../../../../helpers/validation/rules/subscription-gift-form'
import { Route } from '../../../../router'
import { UserContext } from '../../../../stores/User/UserStore'
import CustomDatePicker from '../../../atoms/CustomDatePicker/CustomDatePicker'
import { isValidMemberAge } from "../../../../helpers/validation/age"

const SubscriptionGiftRecipientDetails = () => {

    const { t } = useTranslation()
    const { trackButtonClick } = useGoogleTagManager()

    const navigate = useNavigate()
    const { state: userState } = useContext(UserContext)
    const [recipientName, setRecipientName] = useState(``)
    const [recipientPhoneNumber, setRecipientPhoneNumber] = useState(``)
    const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null)
    const [validationError, setValidationError] = useState(false)
    const [dobValidationError, setDobValidationError] = useState(false)
    const dobValidationErrorMessage = `Please enter a valid date of birth. The recipient must be between 18 and 24 years old to redeem this Subscription.`
    const validationErrorMessage = `Please double check the form for errors`

    useTrackPageView(`Subscription Gift Details Form`)

    const handleSubmit = () => {
        if (areInputsValid()){
            setValidationError(false)
            trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_DETAILS_SUBMIT)
            logLandingPageAction(`User pressed next button on subscription gift details form`, `myInfo`)
            userState.subscriptionGift = {
                ...userState.subscriptionGift,
                recipientName,
                recipientPhoneNumber,
                dateOfBirth: moment(dateOfBirth).format(`DDMMYYYY`),
            }
            navigate(`${Route.SUBSCRIPTION_GIFT_CARD_SELECTION}`)
        }
        else {
            setValidationError(true)
        }
    }

    const handleDateChange = (value: Date | null) => {
        setDateOfBirth(value)
        setDobValidationError(false)
        setValidationError(false)
        if (!isValidMemberAge(value ?? new Date())){
            setDobValidationError(true)
        }
    }

    const areInputsValid = (): boolean => {
        try {
            SUBSCRIPTION_GIFT_FORM.recipientName.parse(recipientName)
            SUBSCRIPTION_GIFT_FORM.phoneNumber.parse(recipientPhoneNumber)
            return !!recipientPhoneNumber
            && !!recipientName
            && !!dateOfBirth
            && !dobValidationError
        } catch (error) {
            return false
        }
    }

    return (
        <Container>
            <CommonBackButton />
            <Spacer height={12} />
            <SubscriptionGiftStepperStep activeStep={0}/>
            <Spacer height={36} />
            <Typography variant="h3">{t(`subscription_gift_details_form_header`)}</Typography>
            <Spacer height={12} />
            <Typography variant="h4">{t(`subscription_gift_details_form_header_2`)}</Typography>
            <Spacer height={24} />
            <Typography variant="h3">{t(`subscription_gift_details_form_header_3`)}</Typography>
            <CustomInput
                fullWidth
                label={t(`name`)}
                onChange={setRecipientName}
                value={recipientName}
                validation={SUBSCRIPTION_GIFT_FORM.recipientName}
                isSignUp={false}
            />
            <CustomInput
                fullWidth
                label={t(`sign_up_phone_number`)}
                onChange={setRecipientPhoneNumber}
                value={recipientPhoneNumber}
                eagerChange
                validation={SUBSCRIPTION_GIFT_FORM.phoneNumber}
                isSignUp={false}
            />
            <CustomDatePicker
                fullWidth
                label={t(`declaration_date_of_birth`)}
                onChange={handleDateChange}
                value={dateOfBirth}
            />
            {dobValidationError && <Typography className={styles.validationError}>{dobValidationErrorMessage}</Typography>}
            {validationError && <Typography className={styles.validationError}>{validationErrorMessage}</Typography>}
            <Button variant='contained' onClick={handleSubmit} fullWidth>{t(`next`)}</Button>
        </Container>
    )
}

export default SubscriptionGiftRecipientDetails